import * as THREE from 'three';

// Shared by aiming, the physical board throw, and the flight into the Core.
export const createAttackStyleAuraScene = (scene) => {
  const aura = new THREE.Group();
  aura.name = 'core-attack-style-aura';
  aura.visible = false;
  scene.add(aura);
  const shellMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      elapsed: { value: 0 },
      strength: { value: 0 },
      motion: { value: 1 },
      guard: { value: 0 },
    },
    vertexShader: `
      uniform float elapsed;
      uniform float motion;
      uniform float guard;
      varying vec3 auraNormal;
      varying vec3 auraPosition;
      varying float height;
      void main() {
        vec3 flame = position;
        float ripple = sin(position.x * 12.0 + elapsed * 7.0)
          * sin(position.z * 11.0 - elapsed * 5.0);
        flame *= 1.0 + ripple * 0.075 * motion;
        flame.y *= mix(1.35 + max(position.y, 0.0) * 0.35, 1.0, guard);
        vec4 world = modelMatrix * vec4(flame, 1.0);
        auraPosition = world.xyz;
        auraNormal = normalize(mat3(modelMatrix) * normal);
        height = position.y;
        gl_Position = projectionMatrix * viewMatrix * world;
      }
    `,
    fragmentShader: `
      uniform float elapsed;
      uniform float strength;
      uniform float motion;
      varying vec3 auraNormal;
      varying vec3 auraPosition;
      varying float height;
      void main() {
        vec3 viewDirection = normalize(cameraPosition - auraPosition);
        float edge = pow(1.0 - max(dot(normalize(auraNormal), viewDirection), 0.0), 1.8);
        float current = 0.85 + 0.15 * sin(height * 16.0 - elapsed * 6.0) * motion;
        float flow = 0.5 + 0.5 * sin(height * 5.0 - elapsed * 2.0 * motion);
        vec3 magic = mix(vec3(1.0, 0.08, 0.16), vec3(0.12, 0.95, 1.0), flow);
        gl_FragColor = vec4(magic, (0.025 + edge * 0.42) * strength * current);
      }
    `,
  });
  aura.add(
    new THREE.Mesh(new THREE.SphereGeometry(0.82, 28, 20), shellMaterial)
  );

  const flameMaterial = new THREE.MeshBasicMaterial({
    color: 0xff294a,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  const flameGeometry = new THREE.ConeGeometry(0.14, 0.9, 5);
  const flames = Array.from({ length: 10 }, (_, index) => {
    const angle = (index / 10) * Math.PI * 2;
    const flame = new THREE.Mesh(flameGeometry, flameMaterial);
    flame.position.set(Math.cos(angle) * 0.64, 0.5, Math.sin(angle) * 0.64);
    flame.rotation.set(Math.sin(angle) * 0.18, angle, -Math.cos(angle) * 0.18);
    aura.add(flame);
    return flame;
  });
  const lightningMaterial = new THREE.LineBasicMaterial({
    color: 0xb3f5ff,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  const lightning = Array.from({ length: 4 }, () => {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(21), 3)
    );
    const bolt = new THREE.Line(geometry, lightningMaterial);
    bolt.frustumCulled = false;
    aura.add(bolt);
    return bolt;
  });
  const emberMaterial = flameMaterial.clone();
  emberMaterial.color.set(0x67f7ff);
  const emberGeometry = new THREE.OctahedronGeometry(0.035);
  const embers = Array.from({ length: 12 }, () => {
    const ember = new THREE.Mesh(emberGeometry, emberMaterial);
    aura.add(ember);
    return ember;
  });
  const light = new THREE.PointLight(0x44eaff, 0, 4, 2);
  aura.add(light);
  let strength = 0;

  return {
    update({
      position,
      style = null,
      elapsed,
      delta,
      reduceMotion,
      visible,
      moving = false,
    }) {
      const guarding = style === 'guard';
      const active = guarding || style === 'overload';
      const target = visible && active ? (guarding ? 0.85 : 1) : 0;
      aura.userData.style = active ? style : null;
      shellMaterial.uniforms.guard.value = guarding ? 1 : 0;
      strength = reduceMotion
        ? target
        : THREE.MathUtils.lerp(strength, target, Math.min(1, delta * 9));
      aura.visible = strength > 0.005;
      if (!aura.visible) return;
      aura.position.copy(position);
      const pulse = reduceMotion ? 0 : Math.sin(elapsed * 8) * 0.035;
      aura.scale.setScalar((guarding ? 1.3 : 1.1) + pulse);
      shellMaterial.uniforms.elapsed.value = elapsed;
      shellMaterial.uniforms.strength.value = strength;
      shellMaterial.uniforms.motion.value = reduceMotion ? 0 : 1;
      flameMaterial.opacity = strength * 0.45;
      emberMaterial.opacity = strength * 0.85;
      lightningMaterial.opacity = strength * (guarding ? 0.3 : 0.85);
      light.intensity = strength * (moving ? 3.4 : 2.2);
      flames.forEach((flame, index) => {
        flame.visible = !reduceMotion && !guarding;
        flame.scale.y = 0.7 + Math.sin(elapsed * 6 + index * 1.9) * 0.3 + 0.36;
      });
      lightning.forEach((bolt, index) => {
        bolt.visible = !reduceMotion;
        if (!bolt.visible) return;
        const positions = bolt.geometry.attributes.position;
        const angle = (index / lightning.length) * Math.PI * 2 + elapsed * 0.6;
        for (let point = 0; point < positions.count; point += 1) {
          const jitter =
            Math.sin(Math.floor(elapsed * 9) * 3 + point * 13 + index) * 0.15;
          const radius = 0.76 + jitter;
          positions.setXYZ(
            point,
            Math.cos(angle) * radius,
            -0.6 + point * 0.3,
            Math.sin(angle) * radius
          );
        }
        positions.needsUpdate = true;
      });
      embers.forEach((ember, index) => {
        ember.visible = !reduceMotion;
        if (!ember.visible) return;
        const progress =
          (elapsed * (moving ? 0.85 : 0.55) + index / embers.length) % 1;
        const angle = index * 2.39996 + elapsed * 0.4;
        const radius = 0.8 + progress * 0.35;
        ember.position.set(
          Math.cos(angle) * radius,
          -0.5 + progress * 2.2,
          Math.sin(angle) * radius
        );
        ember.scale.setScalar(Math.sin(progress * Math.PI) * 1.4);
      });
    },
    dispose() {
      const geometries = new Set();
      const materials = new Set();
      aura.traverse((object) => {
        if (object.geometry) geometries.add(object.geometry);
        if (object.material) materials.add(object.material);
      });
      scene.remove(aura);
      geometries.forEach((geometry) => geometry.dispose());
      materials.forEach((material) => material.dispose());
    },
  };
};
